/**
 * SettingsPage - User Settings & Profile Management
 *
 * Migrated to BasePage architecture.
 * Demonstrates: Protected page, forms, password management, conditional sections.
 */
import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  ArrowLeft,
  Save,
  Camera,
  Mail,
  Phone,
  Building2,
  Loader2,
  Eye,
  EyeOff,
  Lock,
  Check,
} from "lucide-react";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";

// Hooks & Contexts
import {
  useTranslation,
  useSnackbar,
  useDeduplicatedEffect,
} from "@/shared/hooks";
import { usePasswordValidation } from "@/features/auth/hooks";
import { observer } from "mobx-react-lite";
import { useStore } from "@/stores";

// New Design System
import { BasePage } from "@/shared/ui/layout/BasePage";
import { Box, Text } from "@/shared/ui/base";
import { Card, Button } from "@/shared/ui/components";

// Components
import { PasswordRequirement } from "@/features/auth/components";

// Lib
import { auth } from "@/shared/lib/firebase";

// API
import {
  getProfile,
  updateProfile,
  Profile,
  ProfileUpdateRequest,
} from "@/shared/api/profileApi";

// Constants
import { ROUTES } from "@/shared/constants";

/**
 * Check if user is a partner
 */
function isPartner(
  authMe: { orgs?: Array<{ role: string }> } | null | undefined,
): boolean {
  return authMe?.orgs?.some((org) => org.role.startsWith("partner_")) ?? false;
}

export const SettingsPage = observer(function SettingsPage() {
  const t = useTranslation();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { authStore } = useStore();
  const { user, getIdToken, authMe } = authStore;

  // State
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [orgName, setOrgName] = useState("");

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);
  const [newPasswordFocused, setNewPasswordFocused] = useState(false);

  // Password validation
  const passwordValidation = usePasswordValidation(newPassword);

  // Derived
  const userIsPartner = useMemo(() => isPartner(authMe), [authMe]);

  // Check if individual partner
  const isIndividualPartner = useMemo(() => {
    return profile?.onboarding?.is_individual ?? false;
  }, [profile]);

  // Check if password form is valid
  const isPasswordFormValid = useMemo(() => {
    return (
      currentPassword.length > 0 &&
      passwordValidation.isValid &&
      newPassword === confirmPassword &&
      newPassword.length > 0
    );
  }, [
    currentPassword,
    passwordValidation.isValid,
    newPassword,
    confirmPassword,
  ]);

  /**
   * Fetch profile data on mount
   */
  useDeduplicatedEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      const idToken = await getIdToken();
      if (!idToken) {
        setLoading(false);
        return;
      }
      try {
        const profileData = await getProfile(idToken);
        setProfile(profileData);

        // Initialize form values
        setFirstName(profileData.onboarding?.first_name || "");
        setLastName(profileData.onboarding?.last_name || "");
        setPhone(profileData.phone || "");

        // For partners, get org name
        if (profileData.orgs?.[0]?.name) {
          const name = profileData.orgs[0].name;
          if (!name.startsWith("individual-")) {
            setOrgName(name);
          }
        }
      } catch (err) {
        console.error("Failed to fetch profile:", err);
        showSnackbar(t("settingsLoadError"), "error");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
    // Note: showSnackbar and t are stable (from store) so not needed in deps
  }, [getIdToken]);

  /**
   * Handle save profile
   */
  const handleSave = useCallback(async () => {
    setSaving(true);
    const idToken = await getIdToken();
    if (!idToken) {
      setSaving(false);
      return;
    }

    try {
      const updateData: ProfileUpdateRequest = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim() || undefined,
      };

      await updateProfile(idToken, updateData);
      showSnackbar(t("settingsSaved"), "success");
    } catch (err) {
      console.error("Failed to save profile:", err);
      showSnackbar(t("settingsSaveError"), "error");
    } finally {
      setSaving(false);
    }
  }, [getIdToken, firstName, lastName, phone, showSnackbar, t]);

  /**
   * Handle password change
   */
  const handlePasswordChange = useCallback(async () => {
    if (!auth.currentUser || !user?.email) return;
    if (!isPasswordFormValid) return;

    setChangingPassword(true);
    setPasswordChangeSuccess(false);

    try {
      // Re-authenticate with current password
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword,
      );
      await reauthenticateWithCredential(auth.currentUser, credential);

      // Update to new password
      await updatePassword(auth.currentUser, newPassword);

      // Success!
      setPasswordChangeSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      showSnackbar(t("settingsPasswordChanged"), "success");
    } catch (err) {
      console.error("Failed to change password:", err);
      const errorCode = (err as { code?: string })?.code || "";

      if (
        errorCode === "auth/wrong-password" ||
        errorCode === "auth/invalid-credential"
      ) {
        showSnackbar(t("settingsCurrentPasswordWrong"), "error");
      } else if (errorCode === "auth/weak-password") {
        showSnackbar(t("settingsPasswordTooWeak"), "error");
      } else if (errorCode === "auth/requires-recent-login") {
        showSnackbar(t("settingsReauthRequired"), "error");
      } else {
        showSnackbar(t("settingsPasswordChangeError"), "error");
      }
    } finally {
      setChangingPassword(false);
    }
  }, [
    auth.currentUser,
    user?.email,
    currentPassword,
    newPassword,
    isPasswordFormValid,
    showSnackbar,
    t,
  ]);

  /**
   * Handle back to profile
   */
  const handleBack = useCallback(() => {
    navigate(ROUTES.PROFILE);
  }, [navigate]);

  // Check if form has changes
  const hasChanges = useMemo(() => {
    if (!profile) return false;
    return (
      firstName !== (profile.onboarding?.first_name || "") ||
      lastName !== (profile.onboarding?.last_name || "") ||
      phone !== (profile.phone || "")
    );
  }, [profile, firstName, lastName, phone]);

  if (loading) {
    return (
      <BasePage
        title={t("settingsTitle")}
        description="User settings and profile management"
        background="gray"
      >
        <Box className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-red-600" />
        </Box>
      </BasePage>
    );
  }

  return (
    <BasePage
      title={t("settingsTitle")}
      description="User settings and profile management"
      maxWidth="lg"
      background="gray"
    >
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleBack}
        className="mb-6 -ml-2"
      >
        <ArrowLeft className="w-5 h-5 mr-1" />
        {t("backToProfile")}
      </Button>

      {/* Header */}
      <Box className="mb-8">
        <Text variant="h1" className="mb-2">
          {t("settingsTitle")}
        </Text>
        <Text variant="body" color="secondary">
          {t("settingsSubtitle")}
        </Text>
      </Box>

      {/* Profile Picture Section */}
      <Card variant="default" padding="md" className="mb-6">
        <Text variant="h4" className="mb-4">
          {t("settingsProfilePicture")}
        </Text>
        <Box className="flex items-center gap-6">
          <Box className="relative">
            <Box className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center">
              {profile?.photo_url ? (
                <img
                  src={profile.photo_url}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                <User className="w-12 h-12 text-red-600" />
              )}
            </Box>
            <button
              disabled
              className="absolute bottom-0 right-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center border-2 border-white shadow opacity-50 cursor-not-allowed"
              title={t("settingsPhotoComingSoon")}
            >
              <Camera className="w-4 h-4 text-gray-500" />
            </button>
          </Box>
          <Box>
            <Text variant="body" className="text-sm text-gray-500">
              {t("settingsPhotoComingSoon")}
            </Text>
          </Box>
        </Box>
      </Card>

      {/* Personal Information Section */}
      <Card variant="default" padding="md" className="mb-6">
        <Text variant="h4" className="mb-4">
          {t("settingsPersonalInfo")}
        </Text>
        <Box className="space-y-4">
          {/* First Name */}
          <Box>
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t("firstName")}
            </label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder={t("firstName")}
            />
          </Box>

          {/* Last Name */}
          <Box>
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t("lastName")}
            </label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder={t("lastName")}
            />
          </Box>

          {/* Email (read-only) */}
          <Box>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t("email")}
            </label>
            <Box className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                id="email"
                value={user?.email || ""}
                disabled
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            </Box>
            <Text variant="body" className="text-xs text-gray-500 mt-1">
              {t("settingsEmailReadOnly")}
            </Text>
          </Box>

          {/* Phone */}
          <Box>
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t("settingsPhone")}
            </label>
            <Box className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="+81 90-1234-5678"
              />
            </Box>
          </Box>
        </Box>

        {/* Save Profile Button */}
        <Box className="flex justify-end mt-6">
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || !hasChanges}
            loading={saving}
          >
            <Save className="w-5 h-5 mr-1" />
            {t("settingsSave")}
          </Button>
        </Box>
      </Card>

      {/* Organization Section - Partners only */}
      {userIsPartner && (
        <Card variant="default" padding="md" className="mb-6">
          <Text variant="h4" className="mb-4">
            {t("settingsOrganization")}
          </Text>
          {isIndividualPartner ? (
            <Box className="flex items-center gap-3 text-gray-600">
              <User className="w-5 h-5" />
              <Text variant="body">{t("profileIndividual")}</Text>
            </Box>
          ) : (
            <Box>
              <label
                htmlFor="orgName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("orgName")}
              </label>
              <Box className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  id="orgName"
                  value={orgName}
                  disabled
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                />
              </Box>
              <Text variant="body" className="text-xs text-gray-500 mt-1">
                {t("settingsOrgReadOnly")}
              </Text>
            </Box>
          )}
        </Card>
      )}

      {/* Security Section - Change Password */}
      <Card variant="default" padding="md" className="mb-6">
        <Text variant="h4" className="mb-4">
          {t("settingsSecurity")}
        </Text>

        {passwordChangeSuccess && (
          <Box className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <Check className="w-5 h-5 text-green-600" />
            <Text variant="body" className="text-sm text-green-700">
              {t("settingsPasswordChanged")}
            </Text>
          </Box>
        )}

        <Text variant="body" className="text-sm text-gray-600 mb-4">
          {t("settingsChangePasswordDesc")}
        </Text>

        {!showPasswordForm ? (
          // Show just the button to expand the form
          <Button variant="secondary" onClick={() => setShowPasswordForm(true)}>
            <Lock className="w-5 h-5 mr-1" />
            {t("settingsChangePassword")}
          </Button>
        ) : (
          // Show full password change form
          <Box className="space-y-4">
            {/* Current Password */}
            <Box>
              <label
                htmlFor="currentPassword"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("settingsCurrentPassword")}
              </label>
              <Box className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  id="currentPassword"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showCurrentPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </Box>
            </Box>

            {/* New Password */}
            <Box>
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("settingsNewPassword")}
              </label>
              <Box className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showNewPassword ? "text" : "password"}
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onFocus={() => setNewPasswordFocused(true)}
                  onBlur={() => setNewPasswordFocused(false)}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showNewPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </Box>

              {/* Password requirements */}
              {(newPasswordFocused || newPassword.length > 0) && (
                <Box className="mt-2 p-3 bg-gray-50 rounded-lg">
                  <Text
                    variant="caption"
                    weight="medium"
                    color="primary"
                    className="mb-2"
                  >
                    {t("passwordRequirements")}
                  </Text>
                  <PasswordRequirement
                    text={t("passwordReqMin8")}
                    ok={passwordValidation.requirements.minLength}
                  />
                  <PasswordRequirement
                    text={t("passwordReqNumber")}
                    ok={passwordValidation.requirements.hasNumber}
                  />
                  <PasswordRequirement
                    text={t("passwordReqSymbol")}
                    ok={passwordValidation.requirements.hasSymbol}
                  />
                  <PasswordRequirement
                    text={t("passwordReqUpper")}
                    ok={passwordValidation.requirements.hasUppercase}
                  />
                  <PasswordRequirement
                    text={t("passwordReqLower")}
                    ok={passwordValidation.requirements.hasLowercase}
                  />
                </Box>
              )}
            </Box>

            {/* Confirm New Password */}
            <Box>
              <label
                htmlFor="confirmNewPassword"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("confirmPassword")}
              </label>
              <Box className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmNewPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </Box>
              {confirmPassword && newPassword !== confirmPassword && (
                <Text variant="body" className="mt-1 text-sm text-red-600">
                  {t("passwordsDontMatch")}
                </Text>
              )}
            </Box>

            {/* Action Buttons */}
            <Box className="flex gap-3 pt-2">
              <Button
                variant="primary"
                onClick={handlePasswordChange}
                disabled={changingPassword || !isPasswordFormValid}
                loading={changingPassword}
              >
                <Lock className="w-5 h-5 mr-1" />
                {t("settingsChangePassword")}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowPasswordForm(false);
                  setCurrentPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                }}
              >
                {t("back")}
              </Button>
            </Box>
          </Box>
        )}
      </Card>
    </BasePage>
  );
});
